import React, { Fragment } from 'react'
import { browserHistory } from 'react-router'
import { ProfileButton } from '../../features/recumbent/ProfileButton'
import { NotificationsPopover } from './NotificationsPopover'
import { LocaleSwitcherBump } from './LocaleSwitcherBump'
import styles from './GlobalNav.scss'
import { AdminMenu } from './AdminMenu'
import { CoreContext } from 'bundles/core/CoreContext'
import { SigninButton } from 'common/SigninButton'
import { PopoverLinkMenu, PopoverLinkMenuItem } from 'common/styleguide/PopoverLinkMenu'
import { CTA } from 'common/styleguide/CTA'
import { CoreLink } from 'common/CoreLink'
import { Icon } from 'common/Icon'
import { Icon as LegacyIcon } from 'common/LegacyIcon'
import { GlobalSearch } from 'common/GlobalSearch'
import { CHANGE_LOCALE_MODAL, LOCALE_SWITCHER_PREF, LOCALE_BUMP_PREF } from 'common/modals/ChangeLocaleModal'
import { getCurrentUser, getSurrogateLogin, experimentEnabled, currentUserHasPrivilege } from 'lib/utils'
import { photoProfile } from 'lib/urlHelpers'
import { base } from 'lib/styleguide/base.scss'
import widgets from 'lib/styleguide/widgets.scss'
import { featureNames, commonCTAs, Msg, availableLocales, fm } from 'lib/localization'
import { showModal } from 'lib/modalUtils'
import { FormattedMessage as T, injectIntl, defineMessages } from 'react-intl'
import cx from 'classnames'

const currentUserFromConfig = getCurrentUser()
const surrogateLoginFromConfig = getSurrogateLogin()

const labelsForLocale = {
  'en': 'English',
  'en-US': 'English', // backwards compatability
  'en-UK': 'English', // same
  'es': 'Español',
  'fr': 'Français',
  'ja': '日本語',
  'de': 'Deutsch',
  'nl': 'Nederlands',
  'it': 'Italiano',
  'mz': 'ḖḖƞɠŀīşħ'
}

const NavbarLink = (props) => (
  <CoreLink {...props} className={styles.NavbarLink} />
)

const NavbarMenuLink = ({ expanded, ...props }) => (
  <CoreLink {...props} className={cx(styles.NavbarLink, expanded && styles.NavbarLinkExpanded)} />
)

const globalNavMessages = defineMessages({
  logIn: {
    id: 'GlobalNav.logIn',
    defaultMessage: 'Log In'
  },
  news: {
    id: 'featureNames.newsPlusPress',
    defaultMessage: 'News + Press'
  }
})

// this is here because it needs to be defined before the logged in/out items
class _StoriesMenu extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = { expanded: false }
  }

  componentDidMount () {
    this._removeHistoryListener = browserHistory.listen(this.closeMenu)
  }

  componentWillUnMount () {
    this._removeHistoryListener()
  }

  closeMenu = () => {
    this.setState({ expanded: false })
  }

  toggleExpanded = () => {
    this.setState({ expanded: !this.state.expanded })
  }

  render () {
    const { expanded } = this.state
    const { intl } = this.props

    return <Fragment>
      <NavbarMenuLink onClick={this.toggleExpanded} expanded={expanded}>
        <span className={styles.navA} ref={r => { this._ref = r }}>
          <T id='GlobalNav.stories' defaultMessage='Stories' description='Label for a navigation menu that allows users to access the News, Journal, and Ride Reports features' />
          {expanded
            ? <LegacyIcon i='upChevron' size={20} className={styles.jumpTo} />
            : <LegacyIcon i='downChevron' size={20} className={styles.jumpTo} />}
        </span>
      </NavbarMenuLink>
      {expanded && <CoreContext.Consumer>
        {isCore => <PopoverLinkMenu
          target={this._ref}
          placement='bottom'
          onRequestClose={this.toggleExpanded}>
          <CoreContext.Provider value={isCore}>
            <PopoverLinkMenuItem to='/ride_reports'>{fm(intl, featureNames.rideReports)}</PopoverLinkMenuItem>
            <PopoverLinkMenuItem to='/journal'>{fm(intl, featureNames.journal)}</PopoverLinkMenuItem>
            <PopoverLinkMenuItem to='/news'>{fm(intl, globalNavMessages.news)}</PopoverLinkMenuItem>
          </CoreContext.Provider>
        </PopoverLinkMenu>}
      </CoreContext.Consumer>}
    </Fragment>
  }
}

const StoriesMenu = injectIntl(_StoriesMenu)

// these are used while logged into organization accounts
const classicLoggedInItems = <Fragment>
  <NavbarLink href='/find'>Find</NavbarLink>
  <NavbarLink href='/routes/new'>Plan</NavbarLink>
  <NavbarLink href='/log'>Upload</NavbarLink>
  <NavbarLink href='/routes'>Routes</NavbarLink>
  <NavbarLink href='/activities'>Rides</NavbarLink>
  <NavbarLink to='/ride_reports'>Ride Reports</NavbarLink>
  <NavbarLink to='/goals'>Goals</NavbarLink>
  <NavbarLink href='/help'>Help</NavbarLink>
</Fragment>

const loggedInItems = <Fragment>
  <NavbarLink href='/find'><Msg m={featureNames.find} /></NavbarLink>
  <NavbarLink href='/routes/new'><Msg m={featureNames.routePlanner} /></NavbarLink>
  <StoriesMenu />
  <NavbarLink href='/help' id='global-nav-help'><Msg m={featureNames.help} /></NavbarLink>
</Fragment>

const loggedOutItems = <Fragment>
  <NavbarLink href='/features'><Msg m={featureNames.features} /></NavbarLink>
  <NavbarLink href='/upgrade'><Msg m={featureNames.upgrade} /></NavbarLink>
  <NavbarLink href='/route_planner'><Msg m={featureNames.routePlanner} /></NavbarLink>
  <NavbarLink href='/organizations'><Msg m={featureNames.organizations} /></NavbarLink>
  <NavbarLink href='/find'><Msg m={featureNames.find} /></NavbarLink>
  <StoriesMenu />
</Fragment>

class _GlobalNav extends React.PureComponent {
  static defaultProps = {
    currentUser: currentUserFromConfig,
    surrogateLogin: surrogateLoginFromConfig,
    redesignActive: experimentEnabled('homepage_redesign')
  }

  constructor (props) {
    super(props)

    this._localeRef = React.createRef()
    this.state = {
      mobileExpanded: false,
      notificationsExpanded: false,
      allowSearchExpand: true,
      showLanguagePickerPrompt: false
    }
  }

  componentDidMount () {
    this._stopListeningToHistory = browserHistory.listen(this.onLocationChange)
    // min-width is from $navbar-breakpoint in variables.scss
    this._mobileMediaQuery = window.matchMedia('screen and (min-width: 960px)')
    this._mobileMediaQuery.addListener(this.onScreenSizeChange)

    // this is a lot of misery to go through for 191px, but
    // https://github.com/ridewithgps/ridewithgps/issues/7412
    this._goldilocksMediaQuery = window.matchMedia('screen and (min-width: 960px) and (max-width: 1151px)')
    this._goldilocksMediaQuery.addListener(this.onGoldilocksScreenSizeChange)
    if (this._goldilocksMediaQuery.matches) {
      this.onGoldilocksScreenSizeChange({ matches: true })
    }

    if (window.localStorage.getItem(LOCALE_BUMP_PREF)) {
      this.setState({ showLanguagePickerPrompt: true })
      window.localStorage.removeItem(LOCALE_BUMP_PREF)
    }

    const { currentUser, intl } = this.props
    const language = window.navigator.language.slice(0, 2)
    const userLanguage = window.rwgps?.config.language

    let showLocaleSwitcher = currentUser.id &&
      availableLocales.includes(language) &&
      (language !== userLanguage &&
      !(currentUser.preferences || {})[LOCALE_SWITCHER_PREF])

    if (showLocaleSwitcher) {
      showModal(CHANGE_LOCALE_MODAL, {
        intl,
        userLocale: userLanguage ?? 'en',
        navigatorLanguage: language,
        beforeClose: () => this.setLocaleBump(true)
      })
    }
  }

  componentWillUnmount () {
    this._stopListeningToHistory()
    this._mobileMediaQuery.removeListener(this.onScreenSizeChange)
    this._goldilocksMediaQuery.removeListener(this.onGoldilocksScreenSizeChange)
  }

  setLocaleBump = bool => {
    this.setState({ showLanguagePickerPrompt: bool })
  }

  onScreenSizeChange = (e) => {
    const { mobileExpanded } = this.state
    if (e.matches && mobileExpanded) {
      this.toggleMobileNav()
    }
  }

  onGoldilocksScreenSizeChange = (e) => {
    this.setState({ allowSearchExpand: !e.matches })
  }

  // collapse mobile expand when history changes
  onLocationChange = () => {
    const { mobileExpanded } = this.state
    mobileExpanded && this.toggleMobileNav()
  }

  toggleMobileNav = () => {
    const { mobileExpanded } = this.state
    this.setState({ mobileExpanded: !mobileExpanded, notificationsExpanded: false })
  }

  toggleNotifications = () => {
    const { notificationsExpanded } = this.state
    this.setState({ notificationsExpanded: !notificationsExpanded })
  }

  componentDidUpdate () {
    const { mobileExpanded } = this.state
    if (mobileExpanded) {
      document.body.classList.add(styles.noScroll)
    } else {
      document.body.classList.remove(styles.noScroll)
    }
  }

  render () {
    const { currentUser, surrogateLogin, redesignActive, intl } = this.props
    const { mobileExpanded, notificationsExpanded, allowSearchExpand, showLanguagePickerPrompt } = this.state

    const notifications = currentUser.notifications || currentUser.notifications_serialized
    const hasUnseenNotifications = currentUser.numUnreadNotifications > 0

    const navItems = currentUser.id
      ? redesignActive ? loggedInItems : classicLoggedInItems
      : loggedOutItems

    const freeUser = currentUser.id && currentUser.accountLevel <= 0
    const showNotifications = !!currentUser.id

    const className = cx(
      styles.GlobalNav,
      mobileExpanded && styles.mobileExpanded,
      surrogateLogin && styles.surrogateLogin
    )

    const currentLanguage = labelsForLocale[intl.locale]

    return (
      <nav className={className}>
        <div className={styles.navFull}>
          <div className={styles.group}>
            {currentUser.accountLevel === 3 // force <a> if user is club
              ? <a href='/' className={styles.brandingLink}>
                <Icon i='logo-rwgps-simplified' size={40} />
              </a>
              : <CoreLink to='/' className={styles.brandingLink}>
                <Icon i='logo-rwgps-simplified' size={40} />
              </CoreLink>}
            {redesignActive && currentUser.id && <JumpToMenu />}
            {navItems}
          </div>
          <div className={styles.group}>
            {!currentUser.id && <div className={styles.languagePickerButton} onClick={() => showModal(CHANGE_LOCALE_MODAL)}>
              <Icon i='ios-globe-outline' size={24} /> {currentLanguage}
            </div>}
            {currentUser.id && <GlobalSearch intl={intl} currentUser={currentUser} allowSearchExpand={allowSearchExpand} />}
            {freeUser && <NavbarLink href='/choose_account'><Msg m={featureNames.upgrade} /></NavbarLink>}
            {currentUser.accountLevel === 11 && <AdminMenu />}
            {showNotifications && currentUser.id && <a
              onClick={this.toggleNotifications}
              className={cx(styles.iconLink, styles.withMargin)}
              ref={r => { this._notificationIcon = r }}>
              <Icon i='md-notifications' size={24} className={cx(hasUnseenNotifications && styles.hasUnread)} />
            </a>}
            {!currentUser.id && <Fragment>
              <SigninButton id='navbar-signin' buttonText={<Msg m={globalNavMessages.logIn} />} noStyle className={styles.NavbarLink} />
              <CTA id='navbar-signup' inline className={styles.signUp} secondary href='/signup'><Msg capitalized m={commonCTAs.signUp} /></CTA>
            </Fragment>}
            {currentUser.id && <ProfileButton
              ref={this._localeRef}
              intl={intl}
              id='profile-button'
              closeLocaleBump={() => this.setLocaleBump(false)}
              currentUser={currentUser}
              currentLanguage={currentLanguage} />}
          </div>
        </div>
        <div className={styles.navCollapsed}>
          <a onClick={this.toggleMobileNav} className={styles.iconLink}>
            <Icon i={mobileExpanded ? 'md-close' : 'md-menu'} size={24} />
          </a>
          <a href='/' className={styles.brandingLink}>
            <Icon i='logo-rwgps-simplified' size={40} />
          </a>
          {!currentUser.id && <a href='/signup' className={styles.signupCollapsed}><Msg capitalized m={commonCTAs.signUp} /></a>}
          {showNotifications && currentUser.id && <CoreLink to='/notifications' className={styles.iconLink}>
            <Icon i='md-notifications' size={24} className={cx(hasUnseenNotifications && styles.hasUnread)} />
          </CoreLink>}
        </div>
        {mobileExpanded && <GlobalNavMobile
          showUpgrade={freeUser}
          currentUser={currentUser}
          navItems={navItems}
          currentLanguage={currentLanguage} />}
        {notificationsExpanded && <NotificationsPopover
          intl={intl}
          notifications={notifications}
          target={this._notificationIcon}
          onRequestClose={this.toggleNotifications} />}
        {showLanguagePickerPrompt && this._localeRef?.current && <LocaleSwitcherBump
          intl={intl}
          currentUser={currentUser}
          target={this._localeRef?.current}
          onDismiss={() => this.setLocaleBump(false)} />}
      </nav>
    )
  }
}

export const GlobalNav = injectIntl(_GlobalNav)

export class GlobalNavMobile extends React.PureComponent {
  render () {
    const { currentUser, navItems, showUpgrade, currentLanguage } = this.props
    return (
      <div className={cx(base, styles.GlobalNavMobile)}>
        {currentUser.id && <div className={styles.profileImg}>
          <CoreLink to='/'>
            <img src={photoProfile(currentUser.highlightedPhotoId)} />
          </CoreLink>
        </div>}
        <div className={styles.navMain}>
          {currentUser.id && <NavbarLink href='/'><Msg m={featureNames.home} /></NavbarLink>}
          {navItems}
        </div>
        <div className={styles.logout}>
          {!currentUser.id && <SigninButton block id='navbar-signin' buttonText={<Msg m={globalNavMessages.logIn} />} noStyle className={cx(widgets.CTA, widgets.secondaryCTA)} />}
          {currentUser.id && <CTA margins={false} secondary href='/logout'><T id='GlobalNav.logOut' defaultMessage='Log Out' /></CTA>}
          {showUpgrade && <NavbarLink href='/choose_account'><Msg m={featureNames.upgrade} /></NavbarLink>}
          <div className={cx(styles.languagePickerButton, styles.mobile)} onClick={() => showModal(CHANGE_LOCALE_MODAL)}>
            <Icon i='ios-globe-outline' size={24} /> {currentLanguage}
          </div>
        </div>
      </div>
    )
  }
}

class _JumpToMenu extends React.Component {
  constructor (props) {
    super(props)
    this.state = { expanded: false }
  }

  componentDidMount () {
    this._removeHistoryListener = browserHistory.listen(this.closeMenu)
  }

  componentWillUnMount () {
    this._removeHistoryListener()
  }

  closeMenu = () => {
    this.setState({ expanded: false })
  }

  toggleExpanded = () => {
    this.setState({ expanded: !this.state.expanded })
  }

  // ProfileButton uses LegacyIcon, so we have to here for consistency
  render () {
    const { expanded } = this.state
    const { intl } = this.props
    const canSeeRideCenter = currentUserHasPrivilege('activity_center')

    return <Fragment>
      <NavbarMenuLink onClick={this.toggleExpanded} expanded={expanded}>
        <span className={styles.navA} ref={r => { this._ref = r }}>
          <T id='GlobalNav.jumpTo' defaultMessage='Jump To' description='Label for a navigation menu that allows users to quickly access different areas of the site' />
          {expanded
            ? <LegacyIcon i='upChevron' size={20} className={styles.jumpTo} />
            : <LegacyIcon i='downChevron' size={20} className={styles.jumpTo} />}
        </span>
      </NavbarMenuLink>
      {expanded && <CoreContext.Consumer>
        {isCore => <PopoverLinkMenu
          target={this._ref}
          placement='bottom'
          onRequestClose={this.toggleExpanded}>
          <CoreContext.Provider value={isCore}>
            <PopoverLinkMenuItem to='/dashboard'>{fm(intl, featureNames.dashboard)}</PopoverLinkMenuItem>
            <PopoverLinkMenuItem to='/calendar'>{fm(intl, featureNames.calendar)}</PopoverLinkMenuItem>
            <PopoverLinkMenuItem to='/routes'>{fm(intl, featureNames.routes)}</PopoverLinkMenuItem>
            <PopoverLinkMenuItem to='/rides'>{fm(intl, featureNames.rides)}</PopoverLinkMenuItem>
            <PopoverLinkMenuItem to='/collections'>{fm(intl, featureNames.collections)}</PopoverLinkMenuItem>
            <PopoverLinkMenuItem
              href={canSeeRideCenter ? '/activities' : null}
              to={!canSeeRideCenter ? '/analyze' : null}>
              {fm(intl, featureNames.analyze)}
            </PopoverLinkMenuItem>
            <PopoverLinkMenuItem to='/upload'>{fm(intl, featureNames.upload)}</PopoverLinkMenuItem>
            <PopoverLinkMenuItem to='/friends'>{fm(intl, featureNames.friends)}</PopoverLinkMenuItem>
            <PopoverLinkMenuItem to='/more'>{fm(intl, featureNames.more)}</PopoverLinkMenuItem>
          </CoreContext.Provider>
        </PopoverLinkMenu>}
      </CoreContext.Consumer>}
    </Fragment>
  }
}
export const JumpToMenu = injectIntl(_JumpToMenu)
